<template>
  <div class="model-admin-down-container">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".model-admin-down-container"
                @cancel="closeHelp">
      <occupancy-model-admin-down-help :openSection="openHelpSection"></occupancy-model-admin-down-help>
    </res-dialog>
    <div class="grid-controls">
      <div class="historicals-controls">
        <res-radio-group label="Historicals"
                         :value="historicalDataYearsDisplayed"
                         :options="historicalRadioOptions"
                         @input="historicalDataYearsDisplayedChanged">
        </res-radio-group>
      </div>
      <div class="help-controls">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" @click="openHelp(null)" class="pb-2" color="var(--v-resBlue-darken1)">mdi-help-circle</v-icon>
          </template>
          <span>Help</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" class="ml-3 mb-2" :color="commentIconColor" @click="openComments">{{ commentIcon  }}</v-icon>
          </template>
          <span>Comments/Flags</span>
        </v-tooltip>
        <v-menu left offset-y close-on-click @input="updateMenuState">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon :color="menuColor" class="ellipses-control">
              <v-icon dense>{{menuIcon}}</v-icon>
            </v-btn>
          </template>
          <v-list class="ellipses-control-option-list">
            <v-list-item @click="openComments">
              <v-list-item-title class="ellipses-control-option">Add Flag or Comment</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-list-item v-bind="attrs" v-on="on" @click="openClearResetDialog">
                  <v-list-item-title class="ellipses-control-option">Clear & Reset</v-list-item-title>
                </v-list-item>
              </template>
              <span>This will clear this tab of any entered information and revert to the initial values</span>
            </v-tooltip>
          </v-list>
        </v-menu>
      </div>
    </div>
    <div style="height: 980px">
      <ag-grid-vue style="width: 100%; height: 100%;"
                   class="ag-theme-material"
                   :gridOptions="gridOptions"
                   :columnDefs="currentHeaders"
                   :rowData="currentData"
                   @cell-value-changed="onCellValueChanged">
      </ag-grid-vue>
    </div>
    <clear-all-dialog v-if="showClearResetDialog"
                      :showDialog="showClearResetDialog"
                      :maxWidth="480"
                      :setWidth="480"
                      @confirm="confirmClearResetDialog"
                      @cancel="closeClearResetDialog">
    </clear-all-dialog>
  </div>
</template>

<script>
  // Row Types 
  const MODEL_VACANCY_LOSS_TOTAL = 1;
  const MODEL_VACANCY_LOSS_TOTAL_HISTORICAL = 2;
  const ADMIN_VACANCY_LOSS_TOTAL = 3;
  const ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL = 4;
  const DOWN_VACANCY_LOSS_TOTAL = 5;
  const DOWN_VACANCY_LOSS_TOTAL_HISTORICAL = 6;
  const VACANCY_LOSS_TOTAL = 7;
  const VACANCY_LOSS_TOTAL_HISTORICAL = 8;

  const MODEL_COUNT_TOTAL = 9;
  const MODEL_UNIT_TYPES_HEADER = 10;
  const UNIT_TYPE_MODEL_COUNT = 11;
  const UNIT_TYPE_MODEL_COUNT_HISTORICAL = 12;
  const UNIT_TYPE_MODEL_VACANCY_LOSS = 13;
  const UNIT_TYPE_MODEL_VACANCY_LOSS_HISTORICAL = 14;

  const ADMIN_COUNT_TOTAL = 15;
  const ADMIN_UNIT_TYPES_HEADER = 16;
  const UNIT_TYPE_ADMIN_COUNT = 17;
  const UNIT_TYPE_ADMIN_COUNT_HISTORICAL = 18;
  const UNIT_TYPE_ADMIN_VACANCY_LOSS = 19;
  const UNIT_TYPE_ADMIN_VACANCY_LOSS_HISTORICAL = 20;

  const DOWN_COUNT_TOTAL = 21;
  const DOWN_UNIT_TYPES_HEADER = 22;
  const UNIT_TYPE_DOWN_COUNT = 23;
  const UNIT_TYPE_DOWN_COUNT_HISTORICAL = 24;
  const UNIT_TYPE_DOWN_VACANCY_LOSS = 25;
  const UNIT_TYPE_DOWN_VACANCY_LOSS_HISTORICAL = 26;

  const AVERAGE_GPR_PER_UNIT = 27; // hidden

  // Group Tags
  const MODEL_VACANCY_LOSS_TAG = 'model-vacancy-loss';
  const ADMIN_VACANCY_LOSS_TAG = 'admin-vacancy-loss';
  const DOWN_VACANCY_LOSS_TAG = 'down-vacancy-loss';
  const VACANCY_LOSS_TAG = 'vacancy-loss';
  const MODEL_TAG = 'model';
  const ADMIN_TAG = 'admin';
  const DOWN_TAG = 'down';

  import YearGrid from '@/mixins/YearGrid';

  import ResRadioGroup from "@/components/base/ResRadioGroup";
  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog";
  import ResDialog from '@/components/base/ResDialog.vue';
  import { AgGridVue } from "ag-grid-vue";
  import GroupedRowButton from '@/components/base/ag-grid/GroupedRowButton';

  import OccupancyModelAdminDownHelp from '@/components/help/OccupancyModelAdminDownHelp.vue';
  import HelpableMixin from '@/mixins/Helpable.js';

  import budgetService from '@/services/budget';
  import worksheetService from '@/services/worksheet';
  import occupancyService from '@/services/occupancy';
  import budgetGLAccountService from '@/services/budget-gl-account';

  import {Constants} from '@/utilities/constants/index.js';
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'ModelAdminDown',
    mixins: [
      YearGrid,
      HelpableMixin
    ],
    components: {
      ResRadioGroup,
      ClearAllDialog,
      AgGridVue,
      OccupancyModelAdminDownHelp,
      ResDialog,
      /* eslint-disable vue/no-unused-components */
      GroupedRowButton // used by AG Grid
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: { 
        required: true
      },
      commentIteration: {},
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data: function () {
      return {
        isReadonly: false,
        budgetIterationLastModifiedTimestamp: null,
        isCalculating: false,
        summaryRefreshIsQueued: false,
        historicalRadioOptions: [
          { label: 'None', value: 0 },
          { label: '1 Year', value: 1 }
        ],
        historicalDataYearsDisplayed: 0, // default to hiding history
        historicalsLoaded: false,
        fiscalYear: null,
        fiscalYearStartMonth: null,
        fiscalYearHeaders: [],
        availableColumns: [],
        gridOptions: {
          getRowNodeId: this.getRowKey,
          headerHeight: 36,
          rowHeight: 36,
          columnDefs: this.availableColumns,
          alignedGrids: [],
          singleClickEdit: true,
          stopEditingWhenGridLosesFocus: true,
          getRowClass: this.getRowClass,
          isExternalFilterPresent: () => true,
          doesExternalFilterPass: this.shouldShowRow
        },
        modelAdminDownData: [],
        showClearResetDialog: false,
        tabHasUnresolvedComments: false,
        tabHasComments: false,
        menuOpen: false,
        estimateFutureHistoricalOptionMonthOption: 0,
        estimateFutureHistoricalsOccupancyWorkbooksOption: 1,
        startingPeriodIndexToEstimate: 0
      };
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      updateMenuState(isOpen) {
        this.menuOpen = isOpen;
      },
      // DOM load and update logic
      async load() {
        let t = this;

        t.showGridLoadingOverlay(t.gridOptions)

        await t.loadGridConfiguration();
        await t.loadModelAdminDownData(false);

        t.sizeGridColumns(t.gridOptions);
        t.hideGridLoadingOverlay(t.gridOptions);
      },
      async loadGridConfiguration() {
        let t = this;

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        t.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;

        t.estimateFutureHistoricalOptionMonthOption = settings.estimateFutureHistoricalOptionMonthOption;
        t.estimateFutureHistoricalsOccupancyWorkbooksOption = settings.estimateFutureHistoricalsOccupancyWorkbooksOption;
        if (t.fiscalYearStartMonth == 1)
        {                       
          t.startingPeriodIndexToEstimate = t.estimateFutureHistoricalOptionMonthOption - 1;
        }
        else
        {                      
          if (t.fiscalYearStartMonth > t.estimateFutureHistoricalOptionMonthOption)
          {
            t.startingPeriodIndexToEstimate = 12 - t.fiscalYearStartMonth + t.estimateFutureHistoricalOptionMonthOption;
          }
          else
          {
            t.startingPeriodIndexToEstimate = t.estimateFutureHistoricalOptionMonthOption - t.fiscalYearStartMonth;
          }
        }
        
        // setup column headers
        t.fiscalYearHeaders = t.getFiscalYearMonths(t.fiscalYearStartMonth);
        t.availableColumns = [
          { 
            headerName: "", field: "isGroupCollapsed", width: 16, pinned: 'left', suppressSizeToFit: true, 
            cellClass: function(params) { 
              return t.isUniTypesGridHeaderRow(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
            },
            cellStyle: { margin: 0, padding: 0 },
            cellRendererFramework: 'GroupedRowButton',
            cellRendererParams: {
              show: function(params) { 
                return params.data.isGroupParent && (!t.isVacancyLossTotalRow(params.data.rowType) || t.historicalDataYearsDisplayed > 0);
              },
              allCollapsed: function(params) {
                return params.data.isGroupCollapsed;
              },
              onClick: function(params) {
                if (params.data.isGroupParent) {
                  let show = !params.data.isGroupCollapsed; // toggle
                  params.api.forEachNode(node => {
                    if (node.data.groupTag == params.data.groupTag) {
                      node.setDataValue('isGroupCollapsed', show);
                    }
                  });
                  t.refreshDataFilter();
                }
              }
            }
          },
          { 
            headerName: "", field: "primaryLabel", width: 130, pinned: 'left', suppressSizeToFit: true,
            colSpan: function(params) { 
              switch(params.data.rowType) {
                case MODEL_VACANCY_LOSS_TOTAL:
                case MODEL_VACANCY_LOSS_TOTAL_HISTORICAL:
                case ADMIN_VACANCY_LOSS_TOTAL:
                case ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL:
                case DOWN_VACANCY_LOSS_TOTAL:
                case DOWN_VACANCY_LOSS_TOTAL_HISTORICAL:
                case VACANCY_LOSS_TOTAL: 
                case VACANCY_LOSS_TOTAL_HISTORICAL:
                case MODEL_COUNT_TOTAL:
                case ADMIN_COUNT_TOTAL:
                case DOWN_COUNT_TOTAL:
                case UNIT_TYPE_MODEL_VACANCY_LOSS:
                case UNIT_TYPE_MODEL_VACANCY_LOSS_HISTORICAL:
                case UNIT_TYPE_ADMIN_VACANCY_LOSS:
                case UNIT_TYPE_ADMIN_VACANCY_LOSS_HISTORICAL:
                case UNIT_TYPE_DOWN_VACANCY_LOSS:
                case UNIT_TYPE_DOWN_VACANCY_LOSS_HISTORICAL:
                  return 2;
                default:
                  return 1;
              }
            },
            cellClass: function(params) { 
              return t.isUniTypesGridHeaderRow(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
            },
            cellStyle: function(params) {
              var style = { 'margin-left': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'right' };
              if (params.data.primarySubLabel) {
                style['line-height'] = 'normal'; 
              }
              switch (params.data.rowType) {
                case MODEL_UNIT_TYPES_HEADER:
                case ADMIN_UNIT_TYPES_HEADER:
                case DOWN_UNIT_TYPES_HEADER:
                case UNIT_TYPE_MODEL_COUNT:
                case UNIT_TYPE_ADMIN_COUNT:
                case UNIT_TYPE_DOWN_COUNT:
                  style['text-align'] = 'center';
                  break;
              }
              return style;
            },
            cellRenderer: function(params) { 
              let primaryLabelStyle = t.isUniTypesGridHeaderRow(params.data.rowType) ? 'normal' : 'bold';
              return params.data.primarySubLabel ? 
              `<span style="font-weight: ${primaryLabelStyle}">${params.data.primaryLabel}</span>
               <br/>
               <span style="font-weight: normal">${params.data.primarySubLabel}</span>` : 
              `<span>${params.data.primaryLabel}</span>`;
            }
          },
          { 
            headerName: "", field: "secondaryLabel", width: 110, pinned: 'left', suppressSizeToFit: true,
            cellClass: function(params) { 
              return t.isUniTypesGridHeaderRow(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
            },
            cellStyle: function(params) {
                var style = { 'margin-left': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'center' };
                if (params.data.secondarySubLabel) {
                  style['line-height'] = 'normal'; 
                }
                return style;
            },
            cellRenderer: function(params) { 
              return params.data.secondarySubLabel ? 
              `<span style="font-weight: normal">${params.data.secondaryLabel}</span>
               <br/>
               <span style="font-weight: normal">${params.data.secondarySubLabel}</span>` : 
              `<span>${params.data.secondaryLabel}</span>`;
            }
          },
          { 
            headerName: "", field: "year", width: 60, pinned: 'left', suppressSizeToFit: true,
            cellClass: function(params) { 
              return t.isUniTypesGridHeaderRow(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
            },
            cellStyle: function(params) {
                var style = { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold' };
                if (t.isUniTypesGridHeaderRow(params.data.rowType)) {
                  style['font-weight'] = 'normal';
                }
                return style;
            }
          },
          { headerName: t.fiscalYearHeaders[0], field: "periodIndex00Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[1], field: "periodIndex01Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[2], field: "periodIndex02Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[3], field: "periodIndex03Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[4], field: "periodIndex04Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[5], field: "periodIndex05Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[6], field: "periodIndex06Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[7], field: "periodIndex07Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[8], field: "periodIndex08Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[9], field: "periodIndex09Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[10],field: "periodIndex10Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[11],field: "periodIndex11Amount", width: 96, editable: t.isCellEditable, cellClass: t.lockableCellClass, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { 
            headerName: "TOTAL/AVG", field: "totalOrAverage", width: 110, pinned: 'right', valueFormatter: t.cellFormatter,
            cellClass: function(params) { 
              return t.isUniTypesGridHeaderRow(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
            } 
          }
        ];
      },
      lockableCellClass(params) {
        return this.readonly ? (this.isUniTypesGridHeaderRow(params.data.rowType) ? 'sub-header-column' : 'readonly-column') : '';
      },
      async loadModelAdminDownData(includeHistoricals) {
        let t = this;

        let data = await occupancyService.getModelAdminDownEntries(t.budgetIterationId, includeHistoricals);

        t.modelAdminDownData = [];
        // Model Vacancy Loss
        t.modelAdminDownData.push(t.initRow(MODEL_VACANCY_LOSS_TOTAL, null, data.modelVacancyLoss, null));
        if (data.modelVacancyLoss.hasHistoricals) {
          (data.modelVacancyLoss.historicals).forEach((h) => {
            t.modelAdminDownData.push(t.initRow(MODEL_VACANCY_LOSS_TOTAL_HISTORICAL, null, h, null));
          });
        }
        // Admin Vacancy Loss
        t.modelAdminDownData.push(t.initRow(ADMIN_VACANCY_LOSS_TOTAL, null, data.adminVacancyLoss, null));
        if (data.adminVacancyLoss.hasHistoricals) {
          (data.adminVacancyLoss.historicals).forEach((h) => {
            t.modelAdminDownData.push(t.initRow(ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL, null, h, null));
          });
        }
        // Down Vacancy Loss
        t.modelAdminDownData.push(t.initRow(DOWN_VACANCY_LOSS_TOTAL, null, data.downVacancyLoss, null));
        if (data.downVacancyLoss.hasHistoricals) {
          (data.downVacancyLoss.historicals).forEach((h) => {
            t.modelAdminDownData.push(t.initRow(DOWN_VACANCY_LOSS_TOTAL_HISTORICAL, null, h, null));
          });
        }
        // Vacancy Loss
        t.modelAdminDownData.push(t.initRow(VACANCY_LOSS_TOTAL, null, data.vacancyLoss, null));
        if (data.vacancyLoss.hasHistoricals) {
          (data.vacancyLoss.historicals).forEach((h) => {
            t.modelAdminDownData.push(t.initRow(VACANCY_LOSS_TOTAL_HISTORICAL, null, h, null));
          });
        }
        // Model
        t.modelAdminDownData.push(t.initRow(MODEL_COUNT_TOTAL, null, data.modelEntries, null));
        t.modelAdminDownData.push(t.initUnitTypesGridHeaderRow(MODEL_UNIT_TYPES_HEADER));
        (data.modelEntries.unitTypes).forEach((ut) => {
          
          // Average GPR (by unit type)
          t.modelAdminDownData.push(t.initRow(AVERAGE_GPR_PER_UNIT, ut.unitTypeId, ut.averageGrossPotentialRent, null));

          t.modelAdminDownData.push(t.initRow(UNIT_TYPE_MODEL_COUNT, ut.unitTypeId, ut.unitCounts, ut.numberOfUnits));
          if (ut.unitCounts.hasHistoricals) {
            (ut.unitCounts.historicals).forEach((h) => {
              t.modelAdminDownData.push(t.initRow(UNIT_TYPE_MODEL_COUNT_HISTORICAL, ut.unitTypeId, h, null));
            });
          }
          t.modelAdminDownData.push(t.initRow(UNIT_TYPE_MODEL_VACANCY_LOSS, ut.unitTypeId, ut.vacancyLoss, ut.numberOfUnits));
          if (ut.vacancyLoss.hasHistoricals) {
            (ut.vacancyLoss.historicals).forEach((h) => {
              t.modelAdminDownData.push(t.initRow(UNIT_TYPE_MODEL_VACANCY_LOSS_HISTORICAL, ut.unitTypeId, h, null));
            });
          }
        });
        // Admin
        t.modelAdminDownData.push(t.initRow(ADMIN_COUNT_TOTAL, null, data.adminEntries, null));
        t.modelAdminDownData.push(t.initUnitTypesGridHeaderRow(ADMIN_UNIT_TYPES_HEADER));
        (data.adminEntries.unitTypes).forEach((ut) => {
          t.modelAdminDownData.push(t.initRow(UNIT_TYPE_ADMIN_COUNT, ut.unitTypeId, ut.unitCounts, ut.numberOfUnits));
          if (ut.unitCounts.hasHistoricals) {
            (ut.unitCounts.historicals).forEach((h) => {
              t.modelAdminDownData.push(t.initRow(UNIT_TYPE_ADMIN_COUNT_HISTORICAL, ut.unitTypeId, h, null));
            });
          }
          t.modelAdminDownData.push(t.initRow(UNIT_TYPE_ADMIN_VACANCY_LOSS, ut.unitTypeId, ut.vacancyLoss, ut.numberOfUnits));
          if (ut.vacancyLoss.hasHistoricals) {
            (ut.vacancyLoss.historicals).forEach((h) => {
              t.modelAdminDownData.push(t.initRow(UNIT_TYPE_ADMIN_VACANCY_LOSS_HISTORICAL, ut.unitTypeId, h, null));
            });
          }
        });
        // Down
        t.modelAdminDownData.push(t.initRow(DOWN_COUNT_TOTAL, null, data.downEntries, null));
        t.modelAdminDownData.push(t.initUnitTypesGridHeaderRow(DOWN_UNIT_TYPES_HEADER));
        (data.downEntries.unitTypes).forEach((ut) => {
          t.modelAdminDownData.push(t.initRow(UNIT_TYPE_DOWN_COUNT, ut.unitTypeId, ut.unitCounts, ut.numberOfUnits));
          if (ut.unitCounts.hasHistoricals) {
            (ut.unitCounts.historicals).forEach((h) => {
              t.modelAdminDownData.push(t.initRow(UNIT_TYPE_DOWN_COUNT_HISTORICAL, ut.unitTypeId, h, null));
            });
          }
          t.modelAdminDownData.push(t.initRow(UNIT_TYPE_DOWN_VACANCY_LOSS, ut.unitTypeId, ut.vacancyLoss, ut.numberOfUnits));
          if (ut.vacancyLoss.hasHistoricals) {
            (ut.vacancyLoss.historicals).forEach((h) => {
              t.modelAdminDownData.push(t.initRow(UNIT_TYPE_DOWN_VACANCY_LOSS_HISTORICAL, ut.unitTypeId, h, null));
            });
          }
        });
      },
      async refreshModelAdminDownData() {
        let t = this;

        t.showGridLoadingOverlay(t.gridOptions);

        // Retrieve latest data from server
        let response = await occupancyService.getModelAdminDownEntries(t.budgetIterationId, false);

        let rowDataToUpdate = [];
        
        let modelVacancyLossTotalRowData = t.getModelVacancyLossTotalRow().data;
        let adminVacancyLossTotalRowData = t.getAdminVacancyLossTotalRow().data;
        let downVacancyLossTotalRowData = t.getDownVacancyLossTotalRow().data;
        let vacancyLossTotalRowData = t.getVacancyLossTotalRow().data; 
        let modelUnitCountTotalRowData = t.getModelUnitCountTotalRow().data;
        let adminUnitCountTotalRowData = t.getAdminUnitCountTotalRow().data;
        let downUnitCountTotalRowData = t.getDownUnitCountTotalRow().data;
        
        for (let i = 0; i < 12; i++) {
          // Refresh Model Vacancy Loss
          let modelVacancyLossVal = t.getPeriodIndexAmount(response.modelVacancyLoss, i);
          t.setRowNodePeriodIndexAmount(modelVacancyLossTotalRowData, i, modelVacancyLossVal);

          // Refresh Admin Vacancy Loss
          let adminVacancyLossVal = t.getPeriodIndexAmount(response.adminVacancyLoss, i);
          t.setRowNodePeriodIndexAmount(adminVacancyLossTotalRowData, i, adminVacancyLossVal);

          // Refresh Down Vacancy Loss
          let downVacancyLossVal = t.getPeriodIndexAmount(response.downVacancyLoss, i);
          t.setRowNodePeriodIndexAmount(downVacancyLossTotalRowData, i, downVacancyLossVal);

          // Refresh Vacancy Loss
          let totalVacancyLossVal = t.getPeriodIndexAmount(response.vacancyLoss, i);
          t.setRowNodePeriodIndexAmount(vacancyLossTotalRowData, i, totalVacancyLossVal);

          // Refresh Model Units
          let modelUnitCountVal = t.getPeriodIndexAmount(response.modelEntries, i);
          t.setRowNodePeriodIndexAmount(modelUnitCountTotalRowData, i, modelUnitCountVal);
          
          // Refresh Admin Units
          let adminUnitCountVal = t.getPeriodIndexAmount(response.adminEntries, i);
          t.setRowNodePeriodIndexAmount(adminUnitCountTotalRowData, i, adminUnitCountVal);
          
          // Refresh Down Units
          let downUnitCountVal = t.getPeriodIndexAmount(response.downEntries, i);
          t.setRowNodePeriodIndexAmount(downUnitCountTotalRowData, i, downUnitCountVal);
        }

        modelVacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(modelVacancyLossTotalRowData);
        adminVacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(adminVacancyLossTotalRowData);
        downVacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(downVacancyLossTotalRowData);
        vacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(vacancyLossTotalRowData);
        modelUnitCountTotalRowData.totalOrAverage = t.calculateTotalOrAverage(modelUnitCountTotalRowData);
        adminUnitCountTotalRowData.totalOrAverage = t.calculateTotalOrAverage(adminUnitCountTotalRowData);
        downUnitCountTotalRowData.totalOrAverage = t.calculateTotalOrAverage(downUnitCountTotalRowData);
        
        // Queue Refreshed Items for Grid
        rowDataToUpdate.push(modelVacancyLossTotalRowData);
        rowDataToUpdate.push(adminVacancyLossTotalRowData);
        rowDataToUpdate.push(downVacancyLossTotalRowData);
        rowDataToUpdate.push(vacancyLossTotalRowData);
        rowDataToUpdate.push(modelUnitCountTotalRowData);
        rowDataToUpdate.push(adminUnitCountTotalRowData);
        rowDataToUpdate.push(downUnitCountTotalRowData);

        // Refresh Model Unit Types
        (response.modelEntries.unitTypes).forEach((ut) => {
          let utUnitCountRowData = t.getUnitTypeModelUnitCountRow(ut.unitTypeId).data;
          let utVacancyLossRowData = t.getUnitTypeModelVacancyLossRow(ut.unitTypeId).data;
          for (let i = 0; i < 12; i++) {
            let unitCountVal = t.getPeriodIndexAmount(ut.unitCounts, i);
            t.setRowNodePeriodIndexAmount(utUnitCountRowData, i, unitCountVal);

            let vacancyLossVal = t.getPeriodIndexAmount(ut.vacancyLoss, i);
            t.setRowNodePeriodIndexAmount(utVacancyLossRowData, i, vacancyLossVal);
          }
          utUnitCountRowData.totalOrAverage = t.calculateTotalOrAverage(utUnitCountRowData);
          utVacancyLossRowData.totalOrAverage = t.calculateTotalOrAverage(utVacancyLossRowData);

          // Queue Refreshed Items for Grid
          rowDataToUpdate.push(utUnitCountRowData);
          rowDataToUpdate.push(utVacancyLossRowData);
        });

        // Refresh Admin Unit Types
        (response.adminEntries.unitTypes).forEach((ut) => {
          let utUnitCountRowData = t.getUnitTypeAdminUnitCountRow(ut.unitTypeId).data;
          let utVacancyLossRowData = t.getUnitTypeAdminVacancyLossRow(ut.unitTypeId).data;
          for (let i = 0; i < 12; i++) {
            let unitCountVal = t.getPeriodIndexAmount(ut.unitCounts, i);
            t.setRowNodePeriodIndexAmount(utUnitCountRowData, i, unitCountVal);

            let vacancyLossVal = t.getPeriodIndexAmount(ut.vacancyLoss, i);
            t.setRowNodePeriodIndexAmount(utVacancyLossRowData, i, vacancyLossVal);
          }
          utUnitCountRowData.totalOrAverage = t.calculateTotalOrAverage(utUnitCountRowData);
          utVacancyLossRowData.totalOrAverage = t.calculateTotalOrAverage(utVacancyLossRowData);

          // Queue Refreshed Items for Grid
          rowDataToUpdate.push(utUnitCountRowData);
          rowDataToUpdate.push(utVacancyLossRowData);
        });

        // Refresh Down Unit Types
        (response.downEntries.unitTypes).forEach((ut) => {
          let utUnitCountRowData = t.getUnitTypeDownUnitCountRow(ut.unitTypeId).data;
          let utVacancyLossRowData = t.getUnitTypeDownVacancyLossRow(ut.unitTypeId).data;
          for (let i = 0; i < 12; i++) {
            let unitCountVal = t.getPeriodIndexAmount(ut.unitCounts, i);
            t.setRowNodePeriodIndexAmount(utUnitCountRowData, i, unitCountVal);

            let vacancyLossVal = t.getPeriodIndexAmount(ut.vacancyLoss, i);
            t.setRowNodePeriodIndexAmount(utVacancyLossRowData, i, vacancyLossVal);
          }
          utUnitCountRowData.totalOrAverage = t.calculateTotalOrAverage(utUnitCountRowData);
          utVacancyLossRowData.totalOrAverage = t.calculateTotalOrAverage(utVacancyLossRowData);

          // Queue Refreshed Items for Grid
          rowDataToUpdate.push(utUnitCountRowData);
          rowDataToUpdate.push(utVacancyLossRowData);
        });

        // Apply Refreshes to Grid
        t.gridOptions.api.applyTransactionAsync({ update: rowDataToUpdate });

        t.hideGridLoadingOverlay(t.gridOptions);
      },
      async refreshModelAdminDownSummaryData() {
        let t = this;

        // Retrieve latest data from server
        let response = await occupancyService.getModelAdminDownSummary(t.budgetIterationId);
                
        let rowDataToUpdate = [];
        
        let modelVacancyLossTotalRowData = t.getModelVacancyLossTotalRow().data;
        let adminVacancyLossTotalRowData = t.getAdminVacancyLossTotalRow().data;
        let downVacancyLossTotalRowData = t.getDownVacancyLossTotalRow().data;
        let vacancyLossTotalRowData = t.getVacancyLossTotalRow().data; 
        let modelUnitCountTotalRowData = t.getModelUnitCountTotalRow().data;
        let adminUnitCountTotalRowData = t.getAdminUnitCountTotalRow().data;
        let downUnitCountTotalRowData = t.getDownUnitCountTotalRow().data;
        
        for (let i = 0; i < 12; i++) {
          // Refresh Model Vacancy Loss
          let modelVacancyLossVal = t.getPeriodIndexAmount(response.modelVacancyLoss, i);
          t.setRowNodePeriodIndexAmount(modelVacancyLossTotalRowData, i, modelVacancyLossVal);

          // Refresh Admin Vacancy Loss
          let adminVacancyLossVal = t.getPeriodIndexAmount(response.adminVacancyLoss, i);
          t.setRowNodePeriodIndexAmount(adminVacancyLossTotalRowData, i, adminVacancyLossVal);

          // Refresh Down Vacancy Loss
          let downVacancyLossVal = t.getPeriodIndexAmount(response.downVacancyLoss, i);
          t.setRowNodePeriodIndexAmount(downVacancyLossTotalRowData, i, downVacancyLossVal);

          // Refresh Vacancy Loss
          let totalVacancyLossVal = t.getPeriodIndexAmount(response.vacancyLoss, i);
          t.setRowNodePeriodIndexAmount(vacancyLossTotalRowData, i, totalVacancyLossVal);

          // Refresh Model Units
          let modelUnitCountVal = t.getPeriodIndexAmount(response.modelEntries, i);
          t.setRowNodePeriodIndexAmount(modelUnitCountTotalRowData, i, modelUnitCountVal);
          
          // Refresh Admin Units
          let adminUnitCountVal = t.getPeriodIndexAmount(response.adminEntries, i);
          t.setRowNodePeriodIndexAmount(adminUnitCountTotalRowData, i, adminUnitCountVal);
          
          // Refresh Down Units
          let downUnitCountVal = t.getPeriodIndexAmount(response.downEntries, i);
          t.setRowNodePeriodIndexAmount(downUnitCountTotalRowData, i, downUnitCountVal);
        }

        modelVacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(modelVacancyLossTotalRowData);
        adminVacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(adminVacancyLossTotalRowData);
        downVacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(downVacancyLossTotalRowData);
        vacancyLossTotalRowData.totalOrAverage = t.calculateTotalOrAverage(vacancyLossTotalRowData);
        modelUnitCountTotalRowData.totalOrAverage = t.calculateTotalOrAverage(modelUnitCountTotalRowData);
        adminUnitCountTotalRowData.totalOrAverage = t.calculateTotalOrAverage(adminUnitCountTotalRowData);
        downUnitCountTotalRowData.totalOrAverage = t.calculateTotalOrAverage(downUnitCountTotalRowData);
        
        // Queue Refreshed Items for Grid
        rowDataToUpdate.push(modelVacancyLossTotalRowData);
        rowDataToUpdate.push(adminVacancyLossTotalRowData);
        rowDataToUpdate.push(downVacancyLossTotalRowData);
        rowDataToUpdate.push(vacancyLossTotalRowData);
        rowDataToUpdate.push(modelUnitCountTotalRowData);
        rowDataToUpdate.push(adminUnitCountTotalRowData);
        rowDataToUpdate.push(downUnitCountTotalRowData);

        // Apply Refreshes to Grid
        this.gridOptions.api.applyTransactionAsync({ update: rowDataToUpdate });
      },
      async loadHistoricals() {
        let t = this;

        if (t.historicalsLoaded == false) {
          t.showGridLoadingOverlay(t.gridOptions);

          await t.loadModelAdminDownData(true);

          t.sizeGridColumns(t.gridOptions);
          t.hideGridLoadingOverlay(t.gridOptions);

          t.historicalsLoaded = true;
        }
      },
      // row initialization logic
      initRow(rowType, unitTypeId, data, unitTypeNumberOfUnits) {
        let useSecondaryLabel = this.isUnitTypeModelAdminDownCountRow(rowType);
        return {
          // identifiers
          rowType: rowType,
          unitTypeId: unitTypeId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,

          // displayed columns
          primaryLabel: data.label,
          primarySubLabel: useSecondaryLabel ? '' : data.subLabel,
          secondaryLabel: useSecondaryLabel ? data.subLabel : '',
          secondarySubLabel: '',
          year: data.year,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,
          totalOrAverage: this.displaysAverageAsTotal(rowType) ? 
            (this.roundsTotalOrAverage(rowType) ? this.parseInteger(data.average) : this.parseCurrency(data.average)) :
            data.total,
          
          // grouping metadata
          isGroupParent: this.isGroupParentRow(rowType),
          groupTag: this.getRowGroupTag(rowType),
          isGroupCollapsed: !(this.isVacancyLossTotalRow(rowType) || this.isHistoricalVacancyLossTotalRow(rowType)), // all but vacancy loss totals collapsed by default as per product

          // historical metadata
          hasHistoricalRecords: data.hasHistoricals,
          historicalRecordAge: this.fiscalYear - data.year,

          // additional metadata
          unitTypeNumberOfUnits: unitTypeNumberOfUnits
        }
      },
      initUnitTypesGridHeaderRow(rowType) {
        return {
          // identifiers
          rowType: rowType,
          unitTypeId: null,
          worksheetEntryTypeId: null,

          // displayed columns
          primaryLabel: 'Unit',
          primarySubLabel: 'Type',
          secondaryLabel: 'No. of',
          secondarySubLabel: 'Units',
          year: 'Year',
          periodIndex00Amount: this.fiscalYearHeaders[0],
          periodIndex01Amount: this.fiscalYearHeaders[1],
          periodIndex02Amount: this.fiscalYearHeaders[2],
          periodIndex03Amount: this.fiscalYearHeaders[3],
          periodIndex04Amount: this.fiscalYearHeaders[4],
          periodIndex05Amount: this.fiscalYearHeaders[5],
          periodIndex06Amount: this.fiscalYearHeaders[6],
          periodIndex07Amount: this.fiscalYearHeaders[7],
          periodIndex08Amount: this.fiscalYearHeaders[8],
          periodIndex09Amount: this.fiscalYearHeaders[9],
          periodIndex10Amount: this.fiscalYearHeaders[10],
          periodIndex11Amount: this.fiscalYearHeaders[11],
          totalOrAverage: 'TOTAL/AVG',

          // grouping metadata
          isGroupParent: false,
          groupTag: this.getRowGroupTag(rowType),
          isGroupCollapsed: true, // collapsed by default as per product

          // historical metadata
          hasHistoricalRecords: false,
          historicalRecordAge: 0,

          // additional metadata
          unitTypeNumberOfUnits: null
        }
      },
      getRowKey(data) {
        return this.generateRowKey(data.rowType, data.unitTypeId, data.year);
      },
      generateRowKey(rowType, unitTypeId, year) {
        return unitTypeId ? `${unitTypeId.toLowerCase()}-${rowType}-${year}` : `${rowType}-${year}`;
      },
      displaysAverageAsTotal(rowType) {
        switch(rowType) {
          case MODEL_COUNT_TOTAL:
          case UNIT_TYPE_MODEL_COUNT:
          case UNIT_TYPE_MODEL_COUNT_HISTORICAL:
          case ADMIN_COUNT_TOTAL:
          case UNIT_TYPE_ADMIN_COUNT:
          case UNIT_TYPE_ADMIN_COUNT_HISTORICAL:
          case DOWN_COUNT_TOTAL:
          case UNIT_TYPE_DOWN_COUNT:
          case UNIT_TYPE_DOWN_COUNT_HISTORICAL:
            return true;
          default:
            return false;
        }
      },
      roundsTotalOrAverage(rowType) {
        switch(rowType) {
          case MODEL_COUNT_TOTAL:
          case UNIT_TYPE_MODEL_COUNT:
          case UNIT_TYPE_MODEL_COUNT_HISTORICAL:
          case ADMIN_COUNT_TOTAL:
          case UNIT_TYPE_ADMIN_COUNT:
          case UNIT_TYPE_ADMIN_COUNT_HISTORICAL:
          case DOWN_COUNT_TOTAL:
          case UNIT_TYPE_DOWN_COUNT:
          case UNIT_TYPE_DOWN_COUNT_HISTORICAL:
            return true;
          default:
            return false;
        }
      },
      calculateTotalOrAverage(rowData) {
        if (this.displaysAverageAsTotal(rowData.rowType)) {
          return this.roundsTotalOrAverage(rowData.rowType) ? this.calculateAverageAsInteger(rowData) : this.calculateAverageAsCurrency(rowData);
        }
        return this.roundsTotalOrAverage(rowData.rowType) ? this.calculateTotalAsInteger(rowData) : this.calculateTotalAsCurrency(rowData);
      },
      getPeriodIndexAmount(data, periodIndex) { 
        switch (periodIndex) {
          case 0:
            return data.periodIndex00.amount;
          case 1:
            return data.periodIndex01.amount;
          case 2:
            return data.periodIndex02.amount;
          case 3:
            return data.periodIndex03.amount;
          case 4:
            return data.periodIndex04.amount;
          case 5:
            return data.periodIndex05.amount;
          case 6:
            return data.periodIndex06.amount;
          case 7:
            return data.periodIndex07.amount;
          case 8:
            return data.periodIndex08.amount;
          case 9:
            return data.periodIndex09.amount;
          case 10:
            return data.periodIndex10.amount;
          case 11:
            return data.periodIndex11.amount;
          default:
            break;
        }
      },
      getAverageGrossPotentialRentRow(unitTypeId) {
        return this.gridOptions.api.getRowNode(this.generateRowKey(AVERAGE_GPR_PER_UNIT, unitTypeId, this.fiscalYear));
      },
      getModelVacancyLossTotalRow() {
        return this.gridOptions.api.getRowNode(this.generateRowKey(MODEL_VACANCY_LOSS_TOTAL, null, this.fiscalYear));
      },
      getAdminVacancyLossTotalRow() {
        return this.gridOptions.api.getRowNode(this.generateRowKey(ADMIN_VACANCY_LOSS_TOTAL, null, this.fiscalYear)); 
      },
      getDownVacancyLossTotalRow() {
        return this.gridOptions.api.getRowNode(this.generateRowKey(DOWN_VACANCY_LOSS_TOTAL, null, this.fiscalYear));
      },
      getVacancyLossTotalRow() {
        return this.gridOptions.api.getRowNode(this.generateRowKey(VACANCY_LOSS_TOTAL, null, this.fiscalYear));
      },
      getModelUnitCountTotalRow() {
        return this.gridOptions.api.getRowNode(this.generateRowKey(MODEL_COUNT_TOTAL, null, this.fiscalYear));
      },
      getAdminUnitCountTotalRow() {
        return this.gridOptions.api.getRowNode(this.generateRowKey(ADMIN_COUNT_TOTAL, null, this.fiscalYear));
      },
      getDownUnitCountTotalRow() {
        return this.gridOptions.api.getRowNode(this.generateRowKey(DOWN_COUNT_TOTAL, null, this.fiscalYear));
      },
      getUnitTypeModelUnitCountRow(unitTypeId) {
        return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_MODEL_COUNT, unitTypeId, this.fiscalYear));
      },
      getUnitTypeAdminUnitCountRow(unitTypeId) {
        return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_ADMIN_COUNT, unitTypeId, this.fiscalYear));
      },
      getUnitTypeDownUnitCountRow(unitTypeId) {
        return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_DOWN_COUNT, unitTypeId, this.fiscalYear));
      },
      getUnitTypeModelVacancyLossRow(unitTypeId) {
        return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_MODEL_VACANCY_LOSS, unitTypeId, this.fiscalYear));
      },
      getUnitTypeAdminVacancyLossRow(unitTypeId) {
        return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_ADMIN_VACANCY_LOSS, unitTypeId, this.fiscalYear));
      },
      getUnitTypeDownVacancyLossRow(unitTypeId) {
        return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_DOWN_VACANCY_LOSS, unitTypeId, this.fiscalYear));
      },
      getAssociatedUnitTypeVacancyLossRow(unitTypeId, unitCountRowType) {
        switch(unitCountRowType) {
          case UNIT_TYPE_MODEL_COUNT:
            return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_MODEL_VACANCY_LOSS, unitTypeId, this.fiscalYear));
          case UNIT_TYPE_ADMIN_COUNT:
            return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_ADMIN_VACANCY_LOSS, unitTypeId, this.fiscalYear));
          case UNIT_TYPE_DOWN_COUNT:
            return this.gridOptions.api.getRowNode(this.generateRowKey(UNIT_TYPE_DOWN_VACANCY_LOSS, unitTypeId, this.fiscalYear));
          default:
            return null;
        }
      },
      isUniTypesGridHeaderRow(rowType) {
        switch(rowType) {
          case MODEL_UNIT_TYPES_HEADER:
          case ADMIN_UNIT_TYPES_HEADER:
          case DOWN_UNIT_TYPES_HEADER:
            return true;
          default:
            return false;
        }
      },
      isUnitTypeModelAdminDownCountRow(rowType) {
        switch(rowType) {
          case UNIT_TYPE_MODEL_COUNT:
          case UNIT_TYPE_ADMIN_COUNT:
          case UNIT_TYPE_DOWN_COUNT:
            return true;
          default:
            return false;
        }
      },
      isUnitTypeVacancyLossRow(rowType) {
        switch(rowType) {
          case UNIT_TYPE_MODEL_VACANCY_LOSS:
          case UNIT_TYPE_ADMIN_VACANCY_LOSS:
          case UNIT_TYPE_DOWN_VACANCY_LOSS:
            return true;
          default:
            return false;
        }
      },
      isVacancyLossTotalRow(rowType) {
        switch(rowType) {
          case MODEL_VACANCY_LOSS_TOTAL:
          case ADMIN_VACANCY_LOSS_TOTAL:
          case DOWN_VACANCY_LOSS_TOTAL:
          case VACANCY_LOSS_TOTAL:
            return true;
          default:
            return false;
        }
      },
      isHistoricalVacancyLossTotalRow(rowType) {
        switch(rowType) {
          case MODEL_VACANCY_LOSS_TOTAL_HISTORICAL:
          case ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL:
          case DOWN_VACANCY_LOSS_TOTAL_HISTORICAL:
          case VACANCY_LOSS_TOTAL_HISTORICAL:
            return true;
          default:
            return false;
        }
      },
      isGroupParentRow(rowType) {
        switch(rowType) {
          case MODEL_VACANCY_LOSS_TOTAL:
          case ADMIN_VACANCY_LOSS_TOTAL:
          case DOWN_VACANCY_LOSS_TOTAL:
          case VACANCY_LOSS_TOTAL:
          case MODEL_COUNT_TOTAL:
          case ADMIN_COUNT_TOTAL:
          case DOWN_COUNT_TOTAL:
            return true;
          default:
            return false;
        }
      },
      getRowGroupTag(rowType) {
        switch (rowType) {
          case MODEL_VACANCY_LOSS_TOTAL:
          case MODEL_VACANCY_LOSS_TOTAL_HISTORICAL:
            return MODEL_VACANCY_LOSS_TAG;
          case ADMIN_VACANCY_LOSS_TOTAL:
          case ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL:
            return ADMIN_VACANCY_LOSS_TAG;
          case DOWN_VACANCY_LOSS_TOTAL:
          case DOWN_VACANCY_LOSS_TOTAL_HISTORICAL:
            return DOWN_VACANCY_LOSS_TAG;
          case VACANCY_LOSS_TOTAL:
          case VACANCY_LOSS_TOTAL_HISTORICAL:
            return VACANCY_LOSS_TAG;
          case MODEL_COUNT_TOTAL:
          case MODEL_UNIT_TYPES_HEADER:
          case UNIT_TYPE_MODEL_COUNT:
          case UNIT_TYPE_MODEL_COUNT_HISTORICAL:
          case UNIT_TYPE_MODEL_VACANCY_LOSS:
          case UNIT_TYPE_MODEL_VACANCY_LOSS_HISTORICAL:
            return MODEL_TAG;
          case ADMIN_COUNT_TOTAL:
          case ADMIN_UNIT_TYPES_HEADER:
          case UNIT_TYPE_ADMIN_COUNT:
          case UNIT_TYPE_ADMIN_COUNT_HISTORICAL:
          case UNIT_TYPE_ADMIN_VACANCY_LOSS:
          case UNIT_TYPE_ADMIN_VACANCY_LOSS_HISTORICAL:
            return ADMIN_TAG;
          case DOWN_COUNT_TOTAL:
          case DOWN_UNIT_TYPES_HEADER:
          case UNIT_TYPE_DOWN_COUNT:
          case UNIT_TYPE_DOWN_COUNT_HISTORICAL:
          case UNIT_TYPE_DOWN_VACANCY_LOSS:
          case UNIT_TYPE_DOWN_VACANCY_LOSS_HISTORICAL:
            return DOWN_TAG;
          default:
            return null;
        }
      },
      getRowClass(params) {
        switch(params.data.rowType) {
          case MODEL_UNIT_TYPES_HEADER:
          case ADMIN_UNIT_TYPES_HEADER:
          case DOWN_UNIT_TYPES_HEADER:
            return 'sub-header-row';
          case ADMIN_VACANCY_LOSS_TOTAL:
          case DOWN_VACANCY_LOSS_TOTAL:
            return 'readonly-row';
          case MODEL_VACANCY_LOSS_TOTAL:
          case VACANCY_LOSS_TOTAL:
          case MODEL_COUNT_TOTAL:
          case ADMIN_COUNT_TOTAL:
          case DOWN_COUNT_TOTAL:
            return 'readonly-row top-lined-row';
          case MODEL_VACANCY_LOSS_TOTAL_HISTORICAL:
          case ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL:
          case DOWN_VACANCY_LOSS_TOTAL_HISTORICAL:
          case VACANCY_LOSS_TOTAL_HISTORICAL:
          case UNIT_TYPE_MODEL_COUNT_HISTORICAL:
          case UNIT_TYPE_MODEL_VACANCY_LOSS_HISTORICAL:
          case UNIT_TYPE_ADMIN_COUNT_HISTORICAL:
          case UNIT_TYPE_ADMIN_VACANCY_LOSS_HISTORICAL:
          case UNIT_TYPE_DOWN_COUNT_HISTORICAL:
          case UNIT_TYPE_DOWN_VACANCY_LOSS_HISTORICAL:
            return 'readonly-row historical';
          case UNIT_TYPE_MODEL_COUNT:
          case UNIT_TYPE_ADMIN_COUNT:
          case UNIT_TYPE_DOWN_COUNT:
            return 'top-lined-row';
          default:
            return '';
        }
      },
      isCellEditable(params) {
        if (this.readonly) {
          return false;
        }
        if (this.isReadonly || this.isCalculating) {
          return; // view is readonly or is currently calculating
        }
        switch (params.data.rowType) {
          case UNIT_TYPE_MODEL_COUNT:
          case UNIT_TYPE_MODEL_VACANCY_LOSS:
          case UNIT_TYPE_ADMIN_COUNT:
          case UNIT_TYPE_ADMIN_VACANCY_LOSS:
          case UNIT_TYPE_DOWN_COUNT:
          case UNIT_TYPE_DOWN_VACANCY_LOSS:
            break;
          default:
            return false; // can only edit the row types above
        }
        return this.isPeriodIndexAmountColumn(params.colDef.field);
      },
      // row filtering logic
      shouldShowRow(node) {
        return (node.data.isGroupParent || 
        (!node.data.isGroupCollapsed && node.data.historicalRecordAge <= this.historicalDataYearsDisplayed)) &&
        (node.data.rowType != AVERAGE_GPR_PER_UNIT);
      },
      async historicalDataYearsDisplayedChanged(yearsDisplayed) {
        if (yearsDisplayed > 0) {
          await this.loadHistoricals();
        }
        this.historicalDataYearsDisplayed = yearsDisplayed;
        this.refreshDataFilter();
      },
      refreshDataFilter() {
        this.gridOptions.api.onFilterChanged();
      },
      // row update logic
      async onCellValueChanged(params) {
        let t = this;

        // only recalculate when period values have changed
        if (!t.isPeriodIndexAmountColumn(params.colDef.field)) {
          return;
        }

        // only recalculate when unit type level items have been edited
        let unitTypeUnitCountEdited = t.isUnitTypeModelAdminDownCountRow(params.data.rowType);
        let unitTypeVacancyLossEdited = t.isUnitTypeVacancyLossRow(params.data.rowType);
        if (!unitTypeUnitCountEdited && !unitTypeVacancyLossEdited) {
          return;
        }

        t.isCalculating = true;

        let rowType = params.data.rowType;
        let unitTypeId = params.data.unitTypeId;
        let periodIndex = t.getPeriodIndexFromColumn(params.colDef.field);
        let rowNodeDataToUpdate = []; // track updated rows

        if (unitTypeUnitCountEdited) {
          // editing any model, admin or down unit count requires validating it across all 3 groups
          let unitTypeModelUnitCountRowData = rowType == UNIT_TYPE_MODEL_COUNT ? params.data : t.getUnitTypeModelUnitCountRow(unitTypeId).data;
          let unitTypeAdminUnitCountRowData = rowType == UNIT_TYPE_ADMIN_COUNT ? params.data : t.getUnitTypeAdminUnitCountRow(unitTypeId).data;
          let unitTypeDownUnitCountRowData = rowType == UNIT_TYPE_DOWN_COUNT ? params.data : t.getUnitTypeDownUnitCountRow(unitTypeId).data;
          let averageGprRowData = t.getAverageGrossPotentialRentRow(unitTypeId).data;
          let unitTypeVacancyLossRowData = t.getAssociatedUnitTypeVacancyLossRow(unitTypeId, rowType).data;
          
          let modelUnitCountVal = t.parseInteger(t.getRowNodePeriodIndexAmount(unitTypeModelUnitCountRowData, periodIndex));
          let adminUnitCountVal = t.parseInteger(t.getRowNodePeriodIndexAmount(unitTypeAdminUnitCountRowData, periodIndex));
          let downUnitCountVal = t.parseInteger(t.getRowNodePeriodIndexAmount(unitTypeDownUnitCountRowData, periodIndex));
          let totalUnitCountVal = t.parseInteger(params.data.unitTypeNumberOfUnits);

          // these variables allow the validation and calculation logic to be generic for any of the 3 groups
          let rowDataToValidate = null;
          let unitCountToValidate = null;
          let referenceUnitCountA = null;
          let referenceUnitCountB = null;

          switch (rowType) {
            case UNIT_TYPE_MODEL_COUNT:
              rowDataToValidate = unitTypeModelUnitCountRowData;
              unitCountToValidate = modelUnitCountVal;
              referenceUnitCountA = adminUnitCountVal;
              referenceUnitCountB = downUnitCountVal;
              break;
            case UNIT_TYPE_ADMIN_COUNT:
              rowDataToValidate = unitTypeAdminUnitCountRowData;
              unitCountToValidate = adminUnitCountVal;
              referenceUnitCountA = modelUnitCountVal;
              referenceUnitCountB = downUnitCountVal;
              break;
            case UNIT_TYPE_DOWN_COUNT:
              rowDataToValidate = unitTypeDownUnitCountRowData;
              unitCountToValidate = downUnitCountVal;
              referenceUnitCountA = modelUnitCountVal;
              referenceUnitCountB = adminUnitCountVal;
              break;
            default:
              t.isCalculating = false;
              return;
          }

          // revert and display error if the total model/admin/down count is greater than the unit type's total unit count
          let modelAdminDownCount = t.parseInteger(unitCountToValidate + referenceUnitCountA + referenceUnitCountB);
          if (modelAdminDownCount > totalUnitCountVal) {
            // revert change
            t.setRowNodePeriodIndexAmount(rowDataToValidate, periodIndex, params.oldValue);
            t.gridOptions.api.applyTransactionAsync({ update: [rowDataToValidate] });

            // display error
            let validationErrorMessage = 'The number you entered exceeded the number of units available. Please enter a lower number.';
            t.activateErrorDialog({title: 'Model/Admin/Down', message: validationErrorMessage, callback: () => {
              setTimeout(() => {
                t.gridOptions.api.setFocusedCell(params.rowIndex, params.colDef.field); // refocus on cell
                t.gridOptions.api.startEditingCell({ rowIndex: params.rowIndex, colKey: params.colDef.field }); // start editing cell again
              }, 250);
            }, severity: Constants.DIALOG_SEVERITY_INFO });

            t.isCalculating = false;
            return;
          }

          // ensure the edited unit count is not negative
          if (unitCountToValidate < 0) {
            unitCountToValidate = 0;
          }
         
          t.setRowNodePeriodIndexAmount(rowDataToValidate, periodIndex, unitCountToValidate);

          // recalculate vacancy loss
          let averageGprVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(averageGprRowData, periodIndex));
          let unitTypeVacancyLossVal = t.parseCurrency(averageGprVal * unitCountToValidate);
          t.setRowNodePeriodIndexAmount(unitTypeVacancyLossRowData, periodIndex, unitTypeVacancyLossVal);

          // propogate value to future periods
          // loop through all future periods to check value totals and give error at the first point that it's a problem
          var aTest = null;
          var bTest = null;
          var cTest = null;
          var modelAdminDownCountTestOnly = null;
          var averageGprVals = [];
          var unitTypeVacancyLossVals = [];
          for (var p = periodIndex; p < 12; p++) {
            let modelUnitCountValTestOnly = t.parseInteger(t.getRowNodePeriodIndexAmount(unitTypeModelUnitCountRowData, p));
            let adminUnitCountValTestOnly = t.parseInteger(t.getRowNodePeriodIndexAmount(unitTypeAdminUnitCountRowData, p));
            let downUnitCountValTestOnly = t.parseInteger(t.getRowNodePeriodIndexAmount(unitTypeDownUnitCountRowData, p));

            switch (rowType) {
              case UNIT_TYPE_MODEL_COUNT:
                aTest = unitCountToValidate;
                bTest = adminUnitCountValTestOnly;
                cTest = downUnitCountValTestOnly;
                break;
              case UNIT_TYPE_ADMIN_COUNT:
                aTest = unitCountToValidate;
                bTest = modelUnitCountValTestOnly;
                cTest = downUnitCountValTestOnly;
                break;
              case UNIT_TYPE_DOWN_COUNT:
                aTest = unitCountToValidate;
                bTest = modelUnitCountValTestOnly;
                cTest = adminUnitCountValTestOnly;
                break;
              default:
                break;
            }
            // revert and display error if the total model/admin/down count is greater than the unit type's total unit count for this period
            modelAdminDownCountTestOnly = t.parseInteger(aTest + bTest + cTest);
            if (modelAdminDownCountTestOnly > totalUnitCountVal) {  
              // display error              
              let validationErrorMessage2 = `The number you entered exceeded the number of units available for ${t.fiscalYearHeaders[p]}. Please enter a lower number.`;
              let colField = p < 10 ? "periodIndex0" + p + "Amount" : "periodIndex" + p + "Amount"
              t.activateErrorDialog({
                title: 'Model/Admin/Down', message: validationErrorMessage2, callback: () => {
                  setTimeout(() => {
                    t.gridOptions.api.setFocusedCell(params.rowIndex, colField); // refocus on cell
                    t.gridOptions.api.startEditingCell({ rowIndex: params.rowIndex, colKey: colField }); // start editing cell again
                  }, 250);
                }, severity: Constants.DIALOG_SEVERITY_INFO
              });
              // just stop updating the values for the periods
              break;
            }

            t.setRowNodePeriodIndexAmount(rowDataToValidate, p, unitCountToValidate);
         

            averageGprVals[p] = t.parseCurrency(t.getRowNodePeriodIndexAmount(averageGprRowData, p));
            unitTypeVacancyLossVals[p] = t.parseCurrency(averageGprVals[p] * unitCountToValidate);
            t.setRowNodePeriodIndexAmount(unitTypeVacancyLossRowData, p, unitTypeVacancyLossVals[p]);
          }


          // recalculate average unit count
          rowDataToValidate.totalOrAverage = t.calculateAverageAsInteger(rowDataToValidate);

          unitTypeVacancyLossRowData.totalOrAverage = t.calculateTotalAsCurrency(unitTypeVacancyLossRowData);

          // prepare updates for the front-end
          rowNodeDataToUpdate.push(rowDataToValidate);
          rowNodeDataToUpdate.push(unitTypeVacancyLossRowData);
        } else if (unitTypeVacancyLossEdited) {
          // ensure the vacancy loss can parse to a number
          let unitTypeVacancyLossRowData = params.data;
          let vacancyLossVal = t.parseCurrency(params.newValue); 
          t.setRowNodePeriodIndexAmount(unitTypeVacancyLossRowData, periodIndex, vacancyLossVal);

          for (var k = periodIndex; k < 12; k++) {
            t.setRowNodePeriodIndexAmount(unitTypeVacancyLossRowData, k, vacancyLossVal);
          }

          // recalculation total vacancy loss
          unitTypeVacancyLossRowData.totalOrAverage = t.calculateTotalAsCurrency(unitTypeVacancyLossRowData);

          // prepare updates for the front-end
          rowNodeDataToUpdate.push(unitTypeVacancyLossRowData);
        }

        // save updates to the front-end
        if (rowNodeDataToUpdate.length > 0) {
          t.gridOptions.api.applyTransactionAsync({ update: rowNodeDataToUpdate });
        }

        // prepare updates for the backend
        let unitTypeEntriesToPost = [];
        rowNodeDataToUpdate.filter(e => e.worksheetEntryTypeId).forEach((e) => {
          unitTypeEntriesToPost.push({
            unitTypeId: e.unitTypeId,
            worksheetEntryTypeId: e.worksheetEntryTypeId,
            periodIndex00: e.periodIndex00Amount,
            periodIndex01: e.periodIndex01Amount,
            periodIndex02: e.periodIndex02Amount,
            periodIndex03: e.periodIndex03Amount,
            periodIndex04: e.periodIndex04Amount,
            periodIndex05: e.periodIndex05Amount,
            periodIndex06: e.periodIndex06Amount,
            periodIndex07: e.periodIndex07Amount,
            periodIndex08: e.periodIndex08Amount,
            periodIndex09: e.periodIndex09Amount,
            periodIndex10: e.periodIndex10Amount,
            periodIndex11: e.periodIndex11Amount,
          });
        });

        t.isCalculating = false;

        // send updates to the backend
        if (unitTypeEntriesToPost.length > 0) {
          await worksheetService.updateUnitTypeWorksheetEntries(t.budgetIterationId, unitTypeEntriesToPost, t.budgetIterationLastModifiedTimestamp);
        }

        // refresh dependencies
        if (!t.summaryRefreshIsQueued) {
          t.summaryRefreshIsQueued = true;
          setTimeout(async () => {
            t.summaryRefreshIsQueued = false;
            await t.refreshModelAdminDownSummaryData();
            t.$eventHub.$emit('modelAdminDownEntriesUpdated', t.budgetIterationId);
            var worksheetEntryTypeIds = [7, 8, 9, 18, 19, 20];
            /*  
                Enums.WorksheetEntryTypes.NumberOfModelUnits,
                Enums.WorksheetEntryTypes.NumberOfAdminUnits,
                Enums.WorksheetEntryTypes.NumberOfDownUnits,
                Enums.WorksheetEntryTypes.ModelUnitVacancyLossAmount,
                Enums.WorksheetEntryTypes.AdminUnitVacancyLossAmount,
                Enums.WorksheetEntryTypes.DownUnitVacancyLossAmount
            */
            await budgetGLAccountService.updateBasedOnWorksheets(t.budgetIterationId, worksheetEntryTypeIds, [], t.budgetIterationLastModifiedTimestamp);
          }, 2500); // reduce refresh calls when multiple edits happen at once
        }
      },
      cellFormatter(params) {
        let metaFormat;
        switch (params.data.rowType) {
          // dollar amounts
          case MODEL_VACANCY_LOSS_TOTAL:
          case MODEL_VACANCY_LOSS_TOTAL_HISTORICAL:
          case ADMIN_VACANCY_LOSS_TOTAL:
          case ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL:
          case DOWN_VACANCY_LOSS_TOTAL:
          case DOWN_VACANCY_LOSS_TOTAL_HISTORICAL:
          case VACANCY_LOSS_TOTAL:
          case VACANCY_LOSS_TOTAL_HISTORICAL:
          case UNIT_TYPE_MODEL_VACANCY_LOSS:
          case UNIT_TYPE_MODEL_VACANCY_LOSS_HISTORICAL:
          case UNIT_TYPE_ADMIN_VACANCY_LOSS:
          case UNIT_TYPE_ADMIN_VACANCY_LOSS_HISTORICAL:
          case UNIT_TYPE_DOWN_VACANCY_LOSS:
          case UNIT_TYPE_DOWN_VACANCY_LOSS_HISTORICAL:
            metaFormat = this.formatCurrency(params.value, true, true);
            break;
          // integers
          case MODEL_COUNT_TOTAL:
          case UNIT_TYPE_MODEL_COUNT:
          case UNIT_TYPE_MODEL_COUNT_HISTORICAL:
          case ADMIN_COUNT_TOTAL:
          case UNIT_TYPE_ADMIN_COUNT:
          case UNIT_TYPE_ADMIN_COUNT_HISTORICAL:
          case DOWN_COUNT_TOTAL:
          case UNIT_TYPE_DOWN_COUNT:
          case UNIT_TYPE_DOWN_COUNT_HISTORICAL:
            metaFormat = this.formatInteger(params.value);
            break;
          default:
            metaFormat = params.value?.toString();
        }
        let periodIndex = +params.column.colId.replace('periodIndex', '').replace('Amount', '');
        let prefix = (( this.estimateFutureHistoricalsOccupancyWorkbooksOption != 3) && this.isEstimateFutureHistoricalRow(params.data.rowType) && (periodIndex >= this.startingPeriodIndexToEstimate)) ? '' : ''
        return prefix + metaFormat;
      },
      // clear and reset logic
      openClearResetDialog() {
        if (this.readonly) {
          this.$emit('denied-status');
          return;
        }
        this.showClearResetDialog = true;
      },
      closeClearResetDialog() {
        this.showClearResetDialog = false;
      },
      async confirmClearResetDialog() {
        this.changeLoading(true);
        
        let response = await occupancyService.setDefaultModelAdminDownEntries(this.budgetIterationId);
        if (response) {
          await this.refreshModelAdminDownData();
          this.$eventHub.$emit('modelAdminDownEntriesUpdated', this.budgetIterationId);
          this.$eventHub.$emit('showAlertNotification', 'Model/Admin/Down entries cleared and reset successfully!');
        }
        else {
          this.activateErrorDialog({title: 'Clear & Reset Failed', message: 'An error occurred clearing and resetting the data. Please try again.'});
        }
        this.closeClearResetDialog();

        this.changeLoading(false);
      },
      // comments logic
      openComments() {
        this.$emit('openComments', 'modeladmindown');
      },
      async refreshCommentIcon() {
        var commentsResults = await worksheetService.commentsExist(this.budgetIterationId, 4);
        this.tabHasUnresolvedComments = commentsResults.unresolvedFlagExists;
        this.tabHasComments = commentsResults.hasComments;
        console.debug('refresh comment modeladmindown');
      },
      isEstimateFutureHistoricalRow(rowType){
        switch (rowType) {         
          case MODEL_VACANCY_LOSS_TOTAL_HISTORICAL:         
          case ADMIN_VACANCY_LOSS_TOTAL_HISTORICAL:
          case DOWN_VACANCY_LOSS_TOTAL_HISTORICAL:         
          case VACANCY_LOSS_TOTAL_HISTORICAL:        
          case UNIT_TYPE_MODEL_VACANCY_LOSS_HISTORICAL:        
          case UNIT_TYPE_ADMIN_VACANCY_LOSS_HISTORICAL:         
          case UNIT_TYPE_DOWN_VACANCY_LOSS_HISTORICAL:
            return true;        
          default:
            return false;
        }
      }
    },
    computed: {
    ...mapGetters(['currentLoading']),
      currentData() {
        return this.modelAdminDownData;
      },
      currentHeaders() { 
        return this.availableColumns;
      },
      commentIconColor() {
        return this.tabHasUnresolvedComments ? 'var(--v-resRed-darken1)' : 'var(--v-resBlue-darken1)';
      },
      commentIcon() {
        return this.tabHasUnresolvedComments ? 'mdi-flag' : this.tabHasComments ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline';
      },
      menuIcon() {
        return this.menuOpen ? 'mdi-dots-horizontal-circle' : 'mdi-dots-horizontal';
      },
      menuColor() {
        return 'var(--v-resBlue-darken1)';
      }
    },
    watch: {      
      commentIteration: function () {
        this.refreshCommentIcon();
      }
    },
    async mounted() {
      this.changeLoading(true);
      await this.load();
      await this.refreshCommentIcon();
      this.changeLoading(false);
    },
    async created() {
    }
  };
</script>

<style lang="scss" scoped>
.v-input--selection-controls::v-deep {
  margin: 0 !important;
  padding: 0 !important;
}
.grid-controls {
  height: 40px;
  margin-top: 14px;
  margin-bottom: 14px;
}
.historicals-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
.help-controls {
  height: 100%;
  float: right;
  border: solid 1px var(--v-resGrey-lighten3);
  padding: 7px 6px 6px 6px;
  cursor: pointer;
}
.ellipses-control {
  height: 20px;
  width: 20px;
  margin-left: 10px;
  margin-bottom: 8px;
}
.ellipses-control-option-list {
  border-radius: 6px;
  border: 1px solid var(--v-resBlue-darken1);
}
.ellipses-control-option {
  font-size: 13px;
}
</style>
